import rbsLogo from '../assets/rbs.svg';
import natwestLogo from '../assets/natwest.svg';

export type MortgageLinkType =
    | 'ListingsDetailsPage'
    | 'NewHomesListing'
    | 'ResidentialDetailsPage'
    | 'PropertyDetailsPage';

export type MortgageBankInfo = {
    link: string;
    logo: string;
    name: string;
    shortname: 'rbs' | 'natwest';
    altText: string;
};

export const getMortgageLinkType = (
    listingStatus: string,
    listingCondition: string
): MortgageLinkType => {
    if (listingStatus === 'for_sale' && listingCondition === 'new')
        return 'NewHomesListing';
    if (listingStatus === 'for_sale' && listingCondition === 'pre-owned')
        return 'ListingsDetailsPage';
    if (listingStatus === 'to_rent' && listingCondition === 'pre-owned')
        return 'ResidentialDetailsPage';
    if (listingStatus === 'for_sale' && listingCondition === 'pdp_details')
        return 'PropertyDetailsPage';

    return 'ListingsDetailsPage';
};

export const getMortgageCalculatorDetails = (
    region: string | null,
    listingStatus: string,
    listingCondition: string
): MortgageBankInfo => {
    const RBS_BANK = 'RBS';
    const NATWEST_BANK = 'NatWest';

    const RBS_MORTGAGE_LINKS: Record<MortgageLinkType, string> = {
        ListingsDetailsPage:
            'https://ad.doubleclick.net/ddm/trackclk/N1520294.137619ZOOPLA.CO.UK/B26209600.309986211;dc_trk_aid=502509281;dc_trk_cid=155217536;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=?https://personal.rbs.co.uk/personal/mortgages/m/z/for-sale.html?RB-ZW-FS&extcam=R_PAR_ZOOP_Mtg_ZooplaForSale_Web_ALO_AL_OTH_&referrer=rbsprtnrZoopla',
        NewHomesListing:
            'https://ad.doubleclick.net/ddm/trackclk/N1520294.137619ZOOPLA.CO.UK/B26209600.309981558;dc_trk_aid=502509278;dc_trk_cid=155217536;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=?https://personal.rbs.co.uk/personal/mortgages/m/z/new-home.html?RB-ZW-NH&extcam=R_PAR_ZOOP_Mtg_ZooplaNewHome_Web_ALO_AL_OTH_&referrer=rbsprtnrZoopla',
        ResidentialDetailsPage:
            'https://ad.doubleclick.net/ddm/trackclk/N1520294.137619ZOOPLA.CO.UK/B26209600.309981552;dc_trk_aid=502506938;dc_trk_cid=155217536;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=?https://personal.rbs.co.uk/personal/mortgages/m/z/to-rent.html?RB-ZW-R&extcam=R_PAR_ZOOP_Mtg_ZooplaRent_Web_ALO_AL_OTH_&referrer=rbsprtnrZoopla',
        PropertyDetailsPage:
            'https://ad.doubleclick.net/ddm/trackclk/N1520294.137619ZOOPLA.CO.UK/B26209600.309981549;dc_trk_aid=502837275;dc_trk_cid=155217536;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=?https://personal.rbs.co.uk/personal/mortgages/m/z/property-details.html?RB-ZW-PD&extcam=R_PAR_ZOOP_Mtg_ZooplaProperty_Web_ALO_AL_OTH_&referrer=rbsprtnrZoopla'
    };

    const NATWEST_MORTGAGE_LINKS: Record<MortgageLinkType, string> = {
        ListingsDetailsPage:
            'https://ad.doubleclick.net/ddm/trackclk/N1520294.137619ZOOPLA.CO.UK/B26214491.310177693;dc_trk_aid=502839387;dc_trk_cid=155220653;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=?https://personal.natwest.com/personal/mortgages/m/z/for-sale.html?NW-ZW-FS&extcam=N_PAR_ZOOP_Mtg_ZooplaForSale_Web_ALO_AL_OTH_&referrer=nwprtnrZoopla',
        NewHomesListing:
            'https://ad.doubleclick.net/ddm/trackclk/N1520294.137619ZOOPLA.CO.UK/B26214491.309983010;dc_trk_aid=502510526;dc_trk_cid=155220653;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=?https://personal.natwest.com/personal/mortgages/m/z/new-home.html?NW-ZW-NH&extcam=N_PAR_ZOOP_Mtg_ZooplaNewHome_Web_ALO_AL_OTH_&referrer=nwprtnrZoopla',
        ResidentialDetailsPage:
            'https://ad.doubleclick.net/ddm/trackclk/N1520294.137619ZOOPLA.CO.UK/B26214491.309983004;dc_trk_aid=502818571;dc_trk_cid=155220653;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=?https://personal.natwest.com/personal/mortgages/m/z/to-rent.html?NW-ZW-R&extcam=N_PAR_ZOOP_Mtg_ZooplaRent_Web_ALO_AL_OTH_&referrer=nwprtnrZoopla',
        PropertyDetailsPage:
            'https://ad.doubleclick.net/ddm/trackclk/N1520294.137619ZOOPLA.CO.UK/B26214491.309983001;dc_trk_aid=502818568;dc_trk_cid=155220653;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=?https://personal.natwest.com/personal/mortgages/m/z/property-details.html?NW-ZW-PD&extcam=N_PAR_ZOOP_Mtg_ZooplaProperty_Web_ALO_AL_OTH_&referrer=nwprtnrZoopla'
    };

    const listingURL: MortgageLinkType = getMortgageLinkType(
        listingStatus,
        listingCondition
    );

    const RBSBank: MortgageBankInfo = {
        link: RBS_MORTGAGE_LINKS[listingURL],
        logo: rbsLogo,
        shortname: 'rbs',
        name: 'Royal Bank of Scotland',
        altText: 'In partnership with Royal Bank of Scotland'
    };

    const NatWestBank: MortgageBankInfo = {
        link: NATWEST_MORTGAGE_LINKS[listingURL],
        logo: natwestLogo,
        shortname: 'natwest',
        name: 'NatWest',
        altText: 'In partnership with NatWest'
    };

    const Banks = {
        NatWest: NatWestBank,
        RBS: RBSBank
    };

    return region === 'Scotland' ? Banks[RBS_BANK] : Banks[NATWEST_BANK];
};
